import { useMemo } from 'react';
import { Alert, Button, Empty, Space, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { DoohScreen } from '@goelectric/shared-types';
import { DoohScreenStatusBadge } from './DoohBadges';
import { ORIENTATION_LABELS, formatDateTime, formatResolution } from '../utils/format';

export interface ScreensTableProps {
  screens: DoohScreen[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  onSelect: (screen: DoohScreen) => void;
}

/**
 * Tabla de pantallas DOOH (TG-59). El estado (color + icono + texto) y el último
 * contacto se muestran a primera vista, como el semáforo del NOC.
 */
export function ScreensTable({ screens, loading, error, onRetry, onSelect }: ScreensTableProps) {
  const columns = useMemo<ColumnsType<DoohScreen>>(
    () => [
      {
        title: 'Pantalla',
        key: 'name',
        fixed: 'left',
        width: 240,
        render: (_, screen) => (
          <Space direction="vertical" size={0}>
            <Typography.Text strong>{screen.name}</Typography.Text>
            <Typography.Text type="secondary" style={{ fontFamily: 'var(--ge-font-mono)', fontSize: 12 }}>
              {screen.id}
            </Typography.Text>
          </Space>
        ),
      },
      {
        title: 'Estado',
        dataIndex: 'status',
        key: 'status',
        width: 170,
        render: (status) => <DoohScreenStatusBadge status={status} />,
      },
      {
        title: 'Último contacto',
        dataIndex: 'lastSeenAt',
        key: 'lastSeenAt',
        width: 190,
        render: (value: string | null) => formatDateTime(value),
      },
      {
        title: 'Resolución',
        key: 'resolution',
        width: 150,
        render: (_, screen) => formatResolution(screen.resolutionWidth, screen.resolutionHeight),
      },
      {
        title: 'Orientación',
        key: 'orientation',
        width: 130,
        render: (_, screen) => (screen.orientation ? ORIENTATION_LABELS[screen.orientation] : '—'),
      },
      {
        title: 'Zona horaria',
        dataIndex: 'timezone',
        key: 'timezone',
        width: 180,
        render: (value: string | null) => value || '—',
      },
      {
        title: 'Aliado',
        dataIndex: 'allyId',
        key: 'allyId',
        width: 180,
        render: (value: string | null) => value || '—',
      },
      {
        title: 'Acciones',
        key: 'actions',
        width: 90,
        align: 'right',
        render: (_, screen) => (
          <Button size="small" onClick={() => onSelect(screen)}>
            Ver
          </Button>
        ),
      },
    ],
    [onSelect],
  );

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message="No se pudieron cargar las pantallas"
        description={error}
        action={
          <Button size="small" onClick={onRetry}>
            Reintentar
          </Button>
        }
      />
    );
  }

  return (
    <Table<DoohScreen>
      rowKey="id"
      size="middle"
      loading={loading}
      columns={columns}
      dataSource={screens}
      scroll={{ x: 1300 }}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="Aún no hay pantallas registradas. Se registran automáticamente la primera vez que se conectan."
          />
        ),
      }}
    />
  );
}
